import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Save, ImageIcon, Loader2, Search, Trash2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import { reelUploadService } from '../services/reelUploadService';
import { showService, fetchShow } from '../services/showService';
import { createFloatingIcon, updateFloatingIcon, getFloatingIcon, deleteFloatingIcon } from '../services/floatingIconService';
import { Show, DashTenant, tenantDisplayName, tenantToPlatformType } from '../types/Show';
import { DateTimePicker } from '../components/DateTimePicker';

const SHOW_FETCH_LIMIT = 1500;

export default function FloatingIconForm() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const isEdit = Boolean(id);

  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Entry fields
  const [tenant, setTenant] = useState<DashTenant | ''>('');
  const [showId, setShowId] = useState('');
  const [showName, setShowName] = useState<string | null>(null);
  const [iconAssetUrl, setIconAssetUrl] = useState('');
  const [startMs, setStartMs] = useState<number | null>(null);
  const [endMs, setEndMs] = useState<number | null>(null);
  const [enabled, setEnabled] = useState(true);
  const [originalEndAt, setOriginalEndAt] = useState<number | null>(null);

  // Icon upload
  const [iconPreview, setIconPreview] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);

  // Show picker (create mode only)
  const [shows, setShows] = useState<Show[]>([]);
  const [showsLoading, setShowsLoading] = useState(false);
  const [showSearch, setShowSearch] = useState('');

  // Load existing entry in edit mode
  useEffect(() => {
    if (!isEdit || !id) return;
    (async () => {
      try {
        const entry = await getFloatingIcon(id);
        setTenant(entry.tenant);
        setShowId(entry.showId);
        setShowName(entry.showName);
        setIconAssetUrl(entry.iconAssetUrl);
        setIconPreview(entry.iconAssetUrl);
        setStartMs(entry.startAt);
        setEndMs(entry.endAt);
        setOriginalEndAt(entry.endAt);
        setEnabled(entry.enabled);
      } catch {
        toast.error('Failed to load floating-icon entry');
        navigate('/floating-icons');
      } finally {
        setLoading(false);
      }
    })();
  }, [isEdit, id, navigate]);

  // Prefill from query params in create mode (e.g. "Add to floating icon" from a show)
  useEffect(() => {
    if (isEdit) return;
    const prefillTenant = searchParams.get('tenant') as DashTenant | null;
    const prefillShowId = searchParams.get('showId');
    if (prefillTenant) setTenant(prefillTenant);
    if (prefillShowId) {
      setShowId(prefillShowId);
      fetchShow(prefillShowId)
        .then(show => setShowName(show?.name ?? null))
        .catch(() => setShowName(null));
    }
  }, [isEdit, searchParams]);

  // Fetch shows for the chosen tenant (create mode, when no show is pre-selected)
  useEffect(() => {
    if (isEdit || !tenant || showId) return;
    setShowsLoading(true);
    showService
      .getUserShows(SHOW_FETCH_LIMIT, 0, tenantToPlatformType[tenant])
      .then(result => setShows(result ?? []))
      .catch(() => toast.error('Failed to load shows'))
      .finally(() => setShowsLoading(false));
  }, [isEdit, tenant, showId]);

  const filteredShows = useMemo(() => {
    if (!showSearch) return shows.slice(0, 50);
    const term = showSearch.toLowerCase();
    return shows.filter(s => s.name.toLowerCase().includes(term)).slice(0, 50);
  }, [shows, showSearch]);

  const handleTenantChange = (value: DashTenant) => {
    setTenant(value);
    // Reset show selection when tenant changes
    setShowId('');
    setShowName(null);
    setShowSearch('');
  };

  const handleSelectShow = (show: Show) => {
    setShowId(show.id);
    setShowName(show.name);
  };

  const handleIconChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setIconPreview(URL.createObjectURL(file));
    setUploading(true);
    try {
      const { signedUrl } = await reelUploadService.getGeneratedUrlForReel(file.name);
      await reelUploadService.uploadFileToGeneratedUrl(signedUrl, file, {
        'Content-Type': file.type,
        'x-ms-blob-type': 'BlockBlob',
      });
      setIconAssetUrl(signedUrl.split('?')[0]);
      toast.success('Icon uploaded');
    } catch {
      toast.error('Failed to upload icon');
      setIconPreview(iconAssetUrl || null);
    } finally {
      setUploading(false);
    }
  };

  const validate = (): string | null => {
    if (!tenant) return 'Please select a tenant';
    if (!showId) return 'Please select a show';
    if (!iconAssetUrl) return 'Please upload a round icon';
    if (startMs === null) return 'Please set a start time';
    if (endMs !== null && endMs <= startMs) {
      return 'End time must be after start time';
    }
    if (isEdit && originalEndAt !== null && endMs === null) {
      return 'End time cannot be cleared on an existing entry — delete and recreate for open-ended';
    }
    return null;
  };

  const handleSave = async () => {
    const error = validate();
    if (error) {
      toast.error(error);
      return;
    }

    setSaving(true);
    try {
      const startAt = startMs as number; // non-null per validate()
      const endAt = endMs;

      if (isEdit && id) {
        await updateFloatingIcon(id, {
          iconAssetUrl,
          startAt,
          ...(endAt !== null && { endAt }),
          enabled,
        });
        toast.success('Floating icon updated');
      } else {
        await createFloatingIcon({
          showId,
          tenant: tenant as DashTenant,
          iconAssetUrl,
          startAt,
          endAt,
          enabled,
        });
        toast.success('Floating icon created');
      }
      navigate('/floating-icons');
    } catch {
      toast.error(`Failed to ${isEdit ? 'update' : 'create'} floating icon`);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!id || !window.confirm('Delete this floating icon entry? This cannot be undone.')) return;
    setDeleting(true);
    try {
      await deleteFloatingIcon(id);
      toast.success('Floating icon deleted');
      navigate('/floating-icons');
    } catch {
      toast.error('Failed to delete floating icon');
      setDeleting(false);
    }
  };

  const windowError =
    startMs !== null && endMs !== null && endMs <= startMs
      ? 'End time must be after start time'
      : null;

  if (loading) {
    return (
      <div className="max-w-2xl mx-auto p-6 space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto p-6">
      <div className="flex items-center gap-3 mb-8">
        <Button variant="ghost" size="icon" onClick={() => navigate('/floating-icons')}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <h1 className="text-xl font-semibold">
          {isEdit ? 'Edit Floating Icon' : 'New Floating Icon'}
        </h1>
      </div>

      <div className="space-y-6">
        {/* Tenant */}
        <div className="space-y-2">
          <Label>Tenant</Label>
          {isEdit ? (
            <Input value={tenantDisplayName[tenant as DashTenant]} disabled />
          ) : (
            <Select value={tenant} onValueChange={handleTenantChange}>
              <SelectTrigger>
                <SelectValue placeholder="Select tenant" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="REELS">{tenantDisplayName.REELS}</SelectItem>
                <SelectItem value="SHORTFREE">{tenantDisplayName.SHORTFREE}</SelectItem>
              </SelectContent>
            </Select>
          )}
        </div>

        {/* Show */}
        <div className="space-y-2">
          <Label>Show</Label>
          {isEdit || showId ? (
            <div className="flex items-center justify-between rounded-md border bg-secondary/20 px-3 py-2">
              <div>
                <div className="font-medium">{showName ?? '—'}</div>
                <div className="text-xs text-muted-foreground">{showId}</div>
              </div>
              {!isEdit && (
                <Button variant="ghost" size="sm" onClick={() => { setShowId(''); setShowName(null); }}>
                  Change
                </Button>
              )}
            </div>
          ) : !tenant ? (
            <p className="text-sm text-muted-foreground">Select a tenant first to pick a show.</p>
          ) : (
            <div className="space-y-2">
              <div className="relative">
                <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                <Input
                  placeholder="Search shows by name..."
                  className="pl-10"
                  value={showSearch}
                  onChange={e => setShowSearch(e.target.value)}
                />
              </div>
              <div className="max-h-60 overflow-y-auto rounded-md border divide-y">
                {showsLoading ? (
                  <div className="p-4 text-sm text-muted-foreground">Loading shows…</div>
                ) : filteredShows.length === 0 ? (
                  <div className="p-4 text-sm text-muted-foreground">No shows found.</div>
                ) : (
                  filteredShows.map(show => (
                    <button
                      key={show.id}
                      type="button"
                      onClick={() => handleSelectShow(show)}
                      className="w-full text-left px-3 py-2 text-sm hover:bg-secondary/40"
                    >
                      {show.name}
                    </button>
                  ))
                )}
              </div>
            </div>
          )}
        </div>

        {/* Round icon */}
        <div className="space-y-2">
          <Label>Round icon (1:1, ~512×512)</Label>
          <div className="flex items-center gap-4">
            <div className="relative h-24 w-24 rounded-full overflow-hidden border bg-secondary/50 flex items-center justify-center shrink-0">
              {iconPreview ? (
                <img src={iconPreview} alt="Icon preview" className="h-full w-full object-cover" />
              ) : (
                <ImageIcon className="h-8 w-8 text-muted-foreground" />
              )}
              {uploading && (
                <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                  <Loader2 className="h-6 w-6 animate-spin text-white" />
                </div>
              )}
            </div>
            <div>
              <Button
                variant="outline"
                size="sm"
                disabled={uploading}
                onClick={() => document.getElementById('floating-icon-upload')?.click()}
              >
                {iconPreview ? 'Replace' : 'Upload'}
              </Button>
              <input
                id="floating-icon-upload"
                type="file"
                accept="image/png,image/jpeg,image/webp"
                className="hidden"
                onChange={handleIconChange}
              />
            </div>
          </div>
        </div>

        {/* Window */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Start time</Label>
            <DateTimePicker value={startMs} onChange={setStartMs} placeholder="Pick a start date" />
          </div>
          <div className="space-y-2">
            <Label>End time (optional)</Label>
            <DateTimePicker
              value={endMs}
              onChange={setEndMs}
              placeholder="Open-ended"
              disabled={startMs !== null ? d => d.getTime() < new Date(startMs).setHours(0, 0, 0, 0) : undefined}
            />
            {windowError ? (
              <p className="text-xs text-destructive">{windowError}</p>
            ) : (
              <p className="text-xs text-muted-foreground">Leave empty for open-ended (until disabled).</p>
            )}
          </div>
        </div>

        {/* Enabled */}
        <div className="flex items-center justify-between rounded-md border px-3 py-3">
          <div>
            <Label className="font-medium">Enabled</Label>
            <p className="text-xs text-muted-foreground">Disabled entries never show, regardless of window.</p>
          </div>
          <Switch checked={enabled} onCheckedChange={setEnabled} />
        </div>

        {/* Actions */}
        <div className="flex justify-between items-center pt-2">
          <div>
            {isEdit && (
              <Button
                variant="destructive"
                onClick={handleDelete}
                disabled={deleting || saving}
              >
                {deleting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Trash2 className="h-4 w-4 mr-2" />}
                Delete
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => navigate('/floating-icons')} disabled={saving || deleting}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving || uploading || deleting || windowError !== null}>
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
              {isEdit ? 'Save Changes' : 'Create'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
